import React, { useEffect, useRef, useState } from 'react';
import CopyIcon from './icons/CopyIcon';

interface CopyButtonProps {
    text: string;
    copyLabel: string;
    copiedLabel: string;
    disabled?: boolean;
    className?: string;
}

const CopyButton: React.FC<CopyButtonProps> = ({
    text,
    copyLabel,
    copiedLabel,
    disabled = false,
    className = '',
}) => {
    const [copied, setCopied] = useState(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => () => {
        if (timerRef.current) clearTimeout(timerRef.current);
    }, []);

    const handleCopy = () => {
        if (!text.trim() || disabled) return;
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => setCopied(false), 2000);
        }).catch((error) => console.error('Failed to copy', error));
    };

    return (
        <button
            type="button"
            onClick={handleCopy}
            disabled={disabled || !text.trim()}
            className={`p-1.5 rounded-lg transition-all shrink-0 disabled:opacity-40 disabled:cursor-not-allowed ${
                copied
                    ? 'bg-emerald-500 text-white'
                    : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700'
            } ${className}`}
            title={copied ? copiedLabel : copyLabel}
            aria-label={copied ? copiedLabel : copyLabel}
        >
            {copied ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                </svg>
            ) : (
                <CopyIcon className="w-4 h-4" />
            )}
        </button>
    );
};

export default CopyButton;
